import { DefaultTheme } from '@01_people/ui-library';
import deepmerge from 'deepmerge';
import CustomTheme from '../assets/styles/theme';

function hexToHSL(H) {
  // Convert hex to RGB first
  let r = 0;
  let g = 0;
  let b = 0;
  if (H.length === 4) {
    r = `0x${H[1]}${H[1]}`;
    g = `0x${H[2]}${H[2]}`;
    b = `0x${H[3]}${H[3]}`;
  } else if (H.length === 7) {
    r = `0x${H[1]}${H[2]}`;
    g = `0x${H[3]}${H[4]}`;
    b = `0x${H[5]}${H[6]}`;
  }
  // Then to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let h = 0;
  let s = 0;
  let l = 0;

  if (delta === 0) h = 0;
  else if (cmax === r) h = ((g - b) / delta) % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  h = Math.round(h * 60);

  if (h < 0) h += 360;

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return [h, s, l];
}

function generateShades(hex) {
  const colors = {};
  const [h, s] = hexToHSL(hex);

  for (let index = 0; index <= 100; index += 1) {
    colors[index] = `hsla(${h}, ${s}%, ${index}%, 1)`;
  }

  return colors;
}

function generateThemeColors(colors) {
  const types = Object.keys(colors);
  const newColors = {};

  types.forEach(type => {
    newColors[type] = {};
    newColors[type].color = colors[type].color;
    newColors[type].shades = generateShades(newColors[type].color);
  });

  return newColors;
}

export function convertInputsToTheme(colors) {
  return {
    text: {
      borderColor: colors.gray.shades['79'],
      borderColorActive: colors.primary.color,
      borderColorError: colors.error.color,
      borderColorHover: colors.primary.color,

      colorLabel: colors.gray.shades['79'],
      backgroundColor: 'white',
      backgroundColorActive: colors.gray.shades['95'],
      colorText: colors.black.color,
      colorPlaceholder: colors.gray.shades['79'],
      colorPlaceholderActive: colors.gray.color,
      colorReadOnly: colors.gray.color,
      colorError: colors.error.color,

      backgroundColorDisabled: colors.gray.shades['95'],
      colorDisabled: colors.gray.shades['70'],
      errorMessageColor: colors.error.color,

      eyeColor: colors.gray.color,
      eyeColorHover: colors.primary.color
    },
    toggle: {
      backgroundColorChecked: colors.primary.color,
      backgroundColorNotChecked: colors.gray.color,
      backgroundColorDisabled: colors.primary.shades['79'],
      colorDescription: colors.gray.color,
      fontSizeLabel: '20px',
      fontSizeDescription: '16px'
    },
    checkbox: {
      colorNormal: colors.gray.shades['79'],
      colorChecked: colors.primary.color,
      colorReadonly: colors.gray.color,
      colorDisabled: colors.primary.shades['79'],
      colorError: colors.error.color
    }
  };
}

export function convertButtonsToTheme(colors) {
  return {
    primary: {
      // Normal
      color: colors.white.color,
      backgroundColor: colors.primary.color,
      borderSettings: 'none',
      // Hover
      backgroundColorHover: colors.primary.shades['68'],
      // Active
      backgroundColorActive: colors.primary.color,
      // Disabled
      backgroundColorDisabled: colors.primary.shades['95']
    },
    secondary: {
      // Normal
      color: colors.primary.color,
      backgroundColor: 'white',
      borderSettings: `1px solid ${colors.primary.color}`,
      // Hover
      backgroundColorHover: colors.primary.shades['95'],
      // Active
      colorActive: 'white',
      backgroundColorActive: colors.primary.color,
      // Disabled
      colorDisabled: colors.primary.shades['95'],
      backgroundColorDisabled: 'white',
      borderSettingsDisabled: `1px solid ${colors.primary.shades['95']}`
    },
    link: {
      // Normal
      color: colors.primary.color,
      colorHover: colors.primary.color,
      backgroundColor: 'none',
      borderSettings: 'none',
      // Hover
      backgroundColorHover: 'white',
      // Active
      colorActive: colors.primary.color,
      backgroundColorActive: 'white',
      // Disabled
      colorDisabled: colors.primary.shades['95'],
      backgroundColorDisabled: 'none',
      borderSettingsDisabled: 'none'
    }
  };
}

export function convertHeaderToThemed(colors) {
  return {
    backgroundColor: colors.white.color,
    subColor: colors.white.shades['98'],
    linkColor: colors.gray.color,
    linkColorActive: colors.primary.color,
    barColor: colors.primary.color
  };
}

export function convertTypographyToThemed(colors) {
  return {
    colorTextPrimary: colors.black.color,
    colorTextSecondary: colors.gray.color,
    colorTextTertiary: colors.gray.shades['70'],
    colorList: colors.gray.shades['25'],
    formSeparatorColor: colors.gray.shades['90']
  };
}

export function generateTheme(serverTheme) {
  const generatedColors = generateThemeColors(
    deepmerge({ ...CustomTheme.colors }, serverTheme.colors || {})
  );
  return deepmerge(
    {
      ...DefaultTheme,
      ...CustomTheme,
      buttons: convertButtonsToTheme(generatedColors),
      inputs: convertInputsToTheme(generatedColors),
      header: convertHeaderToThemed(generatedColors),
      typography: convertTypographyToThemed(generatedColors),
      colors: generatedColors
    },
    serverTheme
  );
}

export default generateThemeColors;
